import { useEffect, useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { DashboardLayout } from "@/components/DashboardLayout";
import { KCard } from "@/components/KCard";
import { Button } from "@/components/ui/button";
import { UserCog, Save, Search, Shield, Check, ChevronsUpDown, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import type { UiRole } from "@/auth/AuthContext";
import type {
  AdminUserWithPermissions,
  PermissionKey,
} from "@/api/admin/roles-permissions";
import {
  PERMISSION_KEYS,
  getAdminUsersWithPermissions,
  updateUserPermissions,
  updateUserRole,
} from "@/api/admin/roles-permissions";

const permissionsList: Array<{ key: PermissionKey; label: string; desc: string }> = [
  { key: "create_course", label: "Create Course", desc: "Can create and publish new courses" },
  { key: "create_exam", label: "Create Exam", desc: "Can create exams and quizzes" },
  { key: "edit_course", label: "Edit Course", desc: "Can modify existing courses" },
  { key: "manage_students", label: "Manage Students", desc: "Can view and manage student data" },
  { key: "moderate_community", label: "Moderate Community", desc: "Can create/moderate community posts" },
  { key: "view_analytics", label: "View Analytics", desc: "Can view platform analytics" },
  { key: "manage_certificates", label: "Manage Certificates", desc: "Can issue and manage certificates" },
];

function toUiRole(role: AdminUserWithPermissions["role"]): UiRole {
  if (role === "SYSTEM_ADMIN") return "admin";
  if (role === "TEACHER") return "teacher";
  return "student";
}

function toBackendRole(role: UiRole): AdminUserWithPermissions["role"] {
  if (role === "admin") return "SYSTEM_ADMIN";
  if (role === "teacher") return "TEACHER";
  return "STUDENT";
}

function emptyPermissions(): Record<PermissionKey, boolean> {
  return PERMISSION_KEYS.reduce((acc, k) => {
    acc[k] = false;
    return acc;
  }, {} as Record<PermissionKey, boolean>);
}

export default function AssignRolesPage() {
  const queryClient = useQueryClient();

  const [search, setSearch] = useState("");
  const [permSearch, setPermSearch] = useState("");
  const [userPickerOpen, setUserPickerOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);

  // Draft states
  const [roleDraftByUserId, setRoleDraftByUserId] = useState<Record<number, UiRole>>({});
  const [permDraft, setPermDraft] = useState<Record<PermissionKey, boolean>>(() => emptyPermissions());

  // Query
  const { data: usersResponse, isLoading: usersLoading } = useQuery({
    queryKey: ["adminUsersWithPermissions"],
    queryFn: () => getAdminUsersWithPermissions({ page: 1, limit: 100 }),
  });

  const users = useMemo(() => usersResponse?.data ?? [], [usersResponse]);

  // Sync role drafts when users change
  useEffect(() => {
    if (users.length > 0) {
      setRoleDraftByUserId(
        users.reduce((acc, u) => {
          acc[u.id] = toUiRole(u.role);
          return acc;
        }, {} as Record<number, UiRole>)
      );
    }
  }, [users]);

  // Sync perm draft when selected user changes
  const selectedUser = useMemo(
    () => users.find((u) => u.id === selectedUserId) ?? null,
    [users, selectedUserId]
  );

  useEffect(() => {
    if (!selectedUser) {
      setPermDraft(emptyPermissions());
      return;
    }
    setPermDraft({ ...emptyPermissions(), ...(selectedUser.permissions ?? {}) });
  }, [selectedUser]);

  // Default selection
  useEffect(() => {
    if (users.length > 0 && selectedUserId === null) {
      setSelectedUserId(users[0].id);
    }
  }, [users, selectedUserId]);

  // Mutations
  const updateRoleMutation = useMutation({
    mutationFn: ({ userId, role }: { userId: number; role: UiRole }) =>
      updateUserRole(userId, toBackendRole(role)),
    onSuccess: () => {
      toast.success("Role updated");
      queryClient.invalidateQueries({ queryKey: ["adminUsersWithPermissions"] });
    },
    onError: (err: any) => toast.error(err?.message || "Failed to update role"),
  });

  const updatePermissionsMutation = useMutation({
    mutationFn: () => {
      if (!selectedUserId) throw new Error("No user selected");
      return updateUserPermissions(selectedUserId, permDraft);
    },
    onSuccess: () => {
      toast.success("Permissions saved");
      queryClient.invalidateQueries({ queryKey: ["adminUsersWithPermissions"] });
    },
    onError: (err: any) => toast.error(err?.message || "Failed to save permissions"),
  });

  const filtered = useMemo(
    () =>
      users.filter(
        (u) =>
          u.email.toLowerCase().includes(search.toLowerCase()) ||
          String(u.id).includes(search)
      ),
    [users, search]
  );

  const permissionsFilteredUsers = useMemo(() => {
    const q = permSearch.trim().toLowerCase();
    if (!q) return users;
    return users.filter(
      (u) =>
        u.email.toLowerCase().includes(q) ||
        String(u.id).includes(q) ||
        toUiRole(u.role).toLowerCase().includes(q)
    );
  }, [users, permSearch]);

  const roleColors: Record<UiRole, string> = {
    student: "bg-muted text-muted-foreground",
    teacher: "bg-blue-50 text-blue-700",
    admin: "bg-amber-50 text-amber-700",
  };

  return (
    <DashboardLayout>
      <div className="dash-page max-w-5xl">
        <div className="dash-page-header">
          <div>
            <h1 className="dash-page-title">Roles &amp; Permissions</h1>
            <p className="dash-page-subtitle">Manage user roles and configure permissions.</p>
          </div>
        </div>

        <Tabs defaultValue="roles" className="space-y-6">
          <TabsList className="bg-secondary">
            <TabsTrigger value="roles"><UserCog size={14} className="mr-1.5" /> Assign Roles</TabsTrigger>
            <TabsTrigger value="permissions"><Shield size={14} className="mr-1.5" /> Permissions</TabsTrigger>
          </TabsList>

          {/* Assign Roles */}
          <TabsContent value="roles">
            <KCard className="overflow-hidden">
              <div className="p-4 border-b border-border">
                <div className="relative">
                  <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <input
                    type="text"
                    placeholder="Search users..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="w-full h-10 pl-9 pr-4 rounded-lg bg-secondary text-sm outline-none text-foreground placeholder:text-muted-foreground"
                  />
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left text-xs font-bold text-muted-foreground uppercase tracking-wider px-6 py-4">User</th>
                      <th className="text-left text-xs font-bold text-muted-foreground uppercase tracking-wider px-6 py-4">Current Role</th>
                      <th className="text-left text-xs font-bold text-muted-foreground uppercase tracking-wider px-6 py-4">Assign Role</th>
                      <th className="text-right text-xs font-bold text-muted-foreground uppercase tracking-wider px-6 py-4">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {usersLoading ? (
                      <tr>
                        <td className="p-4" colSpan={4}>
                          <div className="dash-empty-state py-6 border-none shadow-none bg-transparent">
                            <Loader2 className="animate-spin mb-2 size-5" />
                            <p className="text-sm font-medium">Loading users...</p>
                          </div>
                        </td>
                      </tr>
                    ) : filtered.length === 0 ? (
                      <tr>
                        <td className="p-4" colSpan={4}>
                          <div className="dash-empty-state py-6 border-none shadow-none bg-transparent">
                            <p className="text-sm font-medium">No users found.</p>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      filtered.map((u) => {
                        const isSaving = updateRoleMutation.isPending && updateRoleMutation.variables?.userId === u.id;
                        return (
                          <tr key={u.id} className="border-b border-border last:border-0 hover:bg-secondary/50 transition-colors">
                            <td className="px-6 py-4">
                              <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary text-xs font-bold">
                                  {u.email?.[0]?.toUpperCase?.() ?? "U"}
                                </div>
                                <div className="min-w-0">
                                  <p className="text-sm font-semibold text-foreground truncate">
                                    {u.email}
                                  </p>
                                  <p className="text-xs text-muted-foreground truncate">{u.email}</p>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <span
                                className={cn(
                                  "px-2.5 py-1 text-xs font-bold rounded-lg capitalize",
                                  roleColors[toUiRole(u.role)]
                                )}
                              >
                                {toUiRole(u.role)}
                              </span>
                            </td>
                            <td className="px-6 py-4">
                              <select
                                value={roleDraftByUserId[u.id] ?? toUiRole(u.role)}
                                onChange={(e) => {
                                  setRoleDraftByUserId((prev) => ({
                                    ...prev,
                                    [u.id]: e.target.value as UiRole,
                                  }));
                                }}
                                disabled={isSaving}
                                className="h-9 px-3 rounded-lg border border-border bg-background text-sm text-foreground"
                              >
                                <option value="student">Student</option>
                                <option value="teacher">Teacher</option>
                                <option value="admin">Admin</option>
                              </select>
                            </td>
                            <td className="px-6 py-4 text-right">
                              <Button
                                size="sm"
                                variant="secondary"
                                disabled={isSaving}
                                onClick={() => updateRoleMutation.mutate({ userId: u.id, role: roleDraftByUserId[u.id] })}
                              >
                                {isSaving ? <Loader2 className="animate-spin size-3" /> : <Save size={14} />}
                                <span className="ml-1.5">Save</span>
                              </Button>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </KCard>
          </TabsContent>

          {/* Permissions */}
          <TabsContent value="permissions">
            <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center">
              <div className="text-sm text-muted-foreground whitespace-nowrap">User</div>
              <div className="flex-1">
                <Popover open={userPickerOpen} onOpenChange={setUserPickerOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={userPickerOpen}
                      disabled={usersLoading}
                      className="w-full justify-between"
                    >
                      <span className="truncate">
                        {selectedUser
                          ? `${selectedUser.email} (${toUiRole(selectedUser.role)})`
                          : "Select a user..."}
                      </span>
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-full p-0">
                    <Command shouldFilter={false}>
                      <CommandInput
                        placeholder="Search by email, id, or role..."
                        value={permSearch}
                        onValueChange={setPermSearch}
                      />
                      <CommandList>
                        <CommandEmpty>No matching users</CommandEmpty>
                        {permissionsFilteredUsers.map((u) => {
                          const isSelected = u.id === selectedUserId;
                          return (
                            <CommandItem
                              key={u.id}
                              value={`${u.email} ${u.id} ${toUiRole(u.role)}`}
                              onSelect={() => {
                                setSelectedUserId(u.id);
                                setUserPickerOpen(false);
                              }}
                            >
                              <Check
                                className={cn(
                                  "mr-2 h-4 w-4",
                                  isSelected ? "opacity-100" : "opacity-0",
                                )}
                              />
                              <div className="min-w-0 flex-1">
                                <div className="truncate text-sm font-medium">
                                  {u.email}
                                </div>
                                <div className="text-xs text-muted-foreground capitalize">
                                  {toUiRole(u.role)}
                                </div>
                              </div>
                            </CommandItem>
                          );
                        })}
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>
              <Button 
                onClick={() => updatePermissionsMutation.mutate()} 
                disabled={!selectedUser || updatePermissionsMutation.isPending}
              >
                {updatePermissionsMutation.isPending ? <Loader2 className="animate-spin size-4 mr-2" /> : <Save size={16} className="mr-2" />}
                Save Permissions
              </Button>
            </div>

            <KCard className="divide-y divide-border">
              {permissionsList.map((p) => (
                <div key={p.key} className="flex items-center justify-between px-6 py-4">
                  <div>
                    <p className="text-sm font-medium text-foreground">{p.label}</p>
                    <p className="text-xs text-muted-foreground">{p.desc}</p>
                  </div>
                  <Switch
                    checked={permDraft[p.key] ?? false}
                    disabled={updatePermissionsMutation.isPending}
                    onCheckedChange={(v) => {
                      setPermDraft((prev) => ({ ...prev, [p.key]: v }));
                    }}
                  />
                </div>
              ))}
            </KCard>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
